import { useEffect, useState } from "react";
import { ShieldCheck, ShieldOff, Smartphone, Loader2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, pesanGalat } from "../lib/api";

/**
 * Keamanan akun · Google Authenticator (keputusan owner 24 Sep 2026).
 *
 * Menyambungkan HP ke akun supaya pemiliknya bisa mereset password sendiri
 * dari halaman login (modal Lupa Password) dengan kode 6 digit. Backend:
 * `routers/authenticator.py`. Konfirmasi dan pemutusan WAJIB password saat
 * ini + kode — sesi yang tertinggal terbuka di komputer toko tidak cukup untuk
 * menyambungkan HP orang lain ke akun ini.
 *
 * Semua tombol selalu terlihat (bukan hover-only) — halaman ini dibuka dari HP.
 *
 * `status.wajib_ganti_password` (tinjauan keamanan 24 Sep 2026): selama
 * password yang berlaku masih password sementara, server menolak memasang —
 * password itu juga diketahui orang yang membuatnya, dan HP yang ia pasang
 * akan bertahan setelah pemiliknya mengganti password.
 */
export default function AuthenticatorSection() {
  const [status, setStatus] = useState(null);
  const [memuat, setMemuat] = useState(true);
  const [pasang, setPasang] = useState(null); // respons /mulai: {qr, rahasia, uri, kedaluwarsa_at, ...}
  const [putus, setPutus] = useState(false);
  const [form, setForm] = useState({ password: "", kode: "" });
  const [sibuk, setSibuk] = useState(false);

  const muatStatus = () =>
    api.get("/auth/authenticator/status")
      .then(r => setStatus(r.data))
      .catch(() => setStatus(null))
      .finally(() => setMemuat(false));
  useEffect(() => { muatStatus(); }, []);

  const kosongkanForm = () => setForm({ password: "", kode: "" });

  const mulai = async () => {
    setSibuk(true);
    try {
      const { data } = await api.post("/auth/authenticator/mulai");
      setPasang(data);
      kosongkanForm();
    } catch (e) {
      toast.error(pesanGalat(e, "Gagal memulai penyambungan"));
    } finally { setSibuk(false); }
  };

  const kirim = async (e, jalur) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(form.kode)) { toast.error("Kode harus 6 digit angka dari aplikasi"); return; }
    setSibuk(true);
    try {
      await api.post(`/auth/authenticator/${jalur}`, form);
      toast.success(jalur === "konfirmasi"
        ? "Google Authenticator tersambung. Anda kini bisa reset password sendiri dari halaman login."
        : "Google Authenticator diputus dari akun ini.");
      setPasang(null);
      setPutus(false);
      kosongkanForm();
      muatStatus();
    } catch (err) {
      toast.error(pesanGalat(err, "Gagal"));
      // Kode yang sudah terkirim tidak bisa dipakai lagi — minta yang terbaru.
      setForm(f => ({ ...f, kode: "" }));
    } finally { setSibuk(false); }
  };

  const jamKedaluwarsa = pasang?.kedaluwarsa_at
    ? new Date(pasang.kedaluwarsa_at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })
    : "";

  const inputPasswordKode = (awalan) => (
    <>
      <label className="block">
        <span className="text-xs font-medium text-slate-600">Password saat ini</span>
        <input type="password" required value={form.password} autoComplete="current-password"
          onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
          data-testid={`${awalan}-password`}
          className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/40" />
      </label>
      <label className="block">
        <span className="text-xs font-medium text-slate-600">Kode 6 digit dari aplikasi</span>
        <input type="text" required value={form.kode} maxLength={6} inputMode="numeric" autoComplete="one-time-code"
          onChange={e => setForm(f => ({ ...f, kode: e.target.value.replace(/\D/g, "").slice(0, 6) }))}
          data-testid={`${awalan}-kode`} placeholder="123456"
          className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-emerald-600/40" />
      </label>
    </>
  );

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm max-w-lg space-y-4"
      data-testid="authenticator-section">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
        <div>
          <h3 className="font-display font-semibold text-slate-900">Keamanan akun · Google Authenticator</h3>
          <p className="text-xs text-slate-500">Reset password sendiri dari halaman login kalau lupa.</p>
        </div>
      </div>

      {memuat ? (
        <p className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Memuat status...</p>
      ) : !status ? (
        <p className="text-sm text-rose-600" data-testid="authenticator-galat">Status tidak bisa dimuat. Muat ulang halaman.</p>
      ) : (
        <>
          <div className="flex items-center gap-2 text-sm" data-testid="authenticator-status">
            {status.aktif ? (
              <span className="px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">Tersambung</span>
            ) : (
              <span className="px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">Belum tersambung</span>
            )}
            {status.aktif && status.disambung_at && (
              <span className="text-xs text-slate-500">sejak {new Date(status.disambung_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}</span>
            )}
          </div>

          {status.perlu_sambung_ulang && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2" data-testid="authenticator-perlu-ulang">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Kode dari HP Anda tidak bisa diperiksa lagi (kunci server berganti). Minta superadmin mereset
                Google Authenticator Anda di Kelola Pengguna, lalu sambungkan ulang.</span>
            </div>
          )}

          {/* Selama password yang berlaku masih password SEMENTARA (NIK / ketikan
              admin), server menolak penyambungan — tombolnya disembunyikan dan
              alasannya dijelaskan, bukan dibiarkan berujung 400. */}
          {!status.aktif && status.wajib_ganti_password && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex gap-2"
              data-testid="authenticator-wajib-ganti-password">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Anda masih memakai password sementara (6 digit NIK atau pemberian admin). Ganti password
                Anda dulu di formulir di atas — setelah itu tombol Sambungkan Google Authenticator muncul di sini.</span>
            </div>
          )}

          {!status.aktif && !pasang && !status.wajib_ganti_password && (
            <>
              <p className="text-sm text-slate-600">
                Sambungkan aplikasi <b>Google Authenticator</b> di HP Anda. Kalau suatu saat lupa password,
                Anda bisa menggantinya sendiri di halaman login memakai kode 6 digit dari aplikasi — tanpa menunggu admin.
              </p>
              <button type="button" onClick={mulai} disabled={sibuk} data-testid="btn-authenticator-sambung"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium disabled:opacity-60">
                {sibuk ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                Sambungkan Google Authenticator
              </button>
            </>
          )}

          {!status.aktif && pasang && !status.wajib_ganti_password && (
            <form onSubmit={e => kirim(e, "konfirmasi")} className="space-y-4" data-testid="authenticator-form-sambung">
              <ol className="list-decimal ml-5 text-sm text-slate-600 space-y-1">
                <li>Buka Google Authenticator, tekan <b>+</b>, pilih <b>Pindai kode QR</b>.</li>
                <li>Pindai kode di bawah ini (berlaku sampai pukul {jamKedaluwarsa}).</li>
                <li>Ketik password Anda dan kode 6 digit yang muncul, lalu tekan <b>Aktifkan</b>.</li>
              </ol>
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <img src={pasang.qr} alt="Kode QR Google Authenticator" data-testid="authenticator-qr"
                  className="w-44 h-44 rounded-lg border border-slate-200 bg-white shrink-0" />
                <div className="text-xs text-slate-600 space-y-2 min-w-0">
                  <p>Tidak bisa memindai? Pilih <b>Masukkan kunci penyiapan</b> dan ketik kunci ini (jenis: berbasis waktu):</p>
                  <p className="font-mono text-sm text-slate-900 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 break-all select-all"
                    data-testid="authenticator-rahasia">{pasang.rahasia}</p>
                  <p>Nama akun di aplikasi: <b>{pasang.penerbit}</b> ({pasang.akun})</p>
                  <a href={pasang.uri} data-testid="authenticator-buka-app"
                    className="inline-flex items-center gap-1 text-sky-700 font-medium underline underline-offset-2">
                    <ExternalLink className="w-3.5 h-3.5" /> Buka langsung di aplikasi (kalau halaman ini dibuka dari HP tersebut)
                  </a>
                </div>
              </div>
              {inputPasswordKode("authenticator")}
              <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={sibuk} data-testid="btn-authenticator-konfirmasi"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-sm font-medium disabled:opacity-60">
                  {sibuk && <Loader2 className="w-4 h-4 animate-spin" />} Aktifkan
                </button>
                <button type="button" onClick={() => { setPasang(null); kosongkanForm(); }} disabled={sibuk}
                  data-testid="btn-authenticator-batal"
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700">Batal</button>
              </div>
            </form>
          )}

          {status.aktif && !putus && (
            <>
              <p className="text-sm text-slate-600">
                Lupa password? Di halaman login tekan <b>Lupa Password?</b> lalu masukkan kode dari aplikasi.
                HP hilang atau ganti HP? Minta superadmin mereset Google Authenticator Anda di Kelola Pengguna,
                atau putuskan di sini selagi HP lama masih di tangan.
              </p>
              <button type="button" onClick={() => { setPutus(true); kosongkanForm(); }} data-testid="btn-authenticator-putuskan"
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 text-sm font-medium">
                <ShieldOff className="w-4 h-4" /> Putuskan
              </button>
            </>
          )}

          {status.aktif && putus && (
            <form onSubmit={e => kirim(e, "putuskan")} className="space-y-4" data-testid="authenticator-form-putuskan">
              <p className="text-sm text-slate-600">Setelah diputus, reset password lewat kode tidak bisa dipakai sampai Anda menyambungkan lagi.</p>
              {inputPasswordKode("authenticator-putuskan")}
              <div className="flex flex-wrap gap-2">
                <button type="submit" disabled={sibuk} data-testid="btn-authenticator-putuskan-konfirmasi"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium disabled:opacity-60">
                  {sibuk && <Loader2 className="w-4 h-4 animate-spin" />} Putuskan sekarang
                </button>
                <button type="button" onClick={() => { setPutus(false); kosongkanForm(); }} disabled={sibuk}
                  data-testid="btn-authenticator-putuskan-batal"
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm text-slate-700">Batal</button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}
